import * as React from "react";
import { Fieldset } from "@trussworks/react-uswds";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { AnalyticsAction, logEvent } from "../../../utils/analytics";
import { 
  SearchFilters,
 } from "../../../types/v1_schemas";
import FilterCheckbox from "./FilterCheckbox";
import { toggleItemsInFilterList } from "../../../utils/filters/shared";
import { AxiosResponse } from "axios";
import i18n from "../../../i18n";
import { QueryLookupTablesApi } from "../../../utils/QueryHelper";
import { CrisisContext } from "../../../pages/Search/Search";

const serviceMatch = ["Screening and early prevention for mental health", "Detección y prevención temprana de problemas de salud mental",
  "Walk-in crisis services", "Servicios sin cita previa en caso de crisis",
  "Withdrawal management (i.e. detox)", "Manejo de la abstinencia (es decir, desintoxicación)", 
  "Opioid use disorder treatment", "Tratamiento de trastornos por consumo de opioides",
  "Residential for mental health", "Residencias para la salud mental",
  "Residential for substance use", "Residencias por el consumo de sustancias",
  "DUI Level I/II Education and Therapy", "Educación y terapia de nivel I/II por DUI"];

const excludedServices: string[] = ["Adult eating disorder treatment (18+)", "Tratamiento de trastornos alimenticios en adultos (mayores de 18 años)", 
  "Eating disorder treatment – outpatient (care without staying in the hospital)", "Tratamiento de trastornos alimenticios: ambulatorio (atención médica sin hospitalización)", 
  "Eating disorder treatment - overnight within a hospital setting", "Tratamiento de trastornos alimenticios: nocturno en un entorno hospitalario", 
  "Eating disorder treatment - residential (overnight care)", "Tratamiento de trastornos alimenticios: residencial (atención médica nocturna)", 
  "Eating disorder treatment – structured outpatient (planned program of regular care without staying in the hospital)", "Tratamiento de trastornos alimenticios: ambulatorio (atención médica sin hospitalización)", 
  "Eating disorder treatment – structured outpatient (planned program of regular care with partial stay in the hospital)", "Tratamiento de trastornos alimenticios: ambulatorio estructurado (programa planificado de atención médica regular con hospitalización parcial)", 
  "Youth eating disorder treatment", "Tratamiento de trastornos alimenticios en jóvenes", "Psychedelic-assisted_therapies", "Ketamine-assisted_therapy"];

type ApiServiceProvided = {
  id: number;
  categoryId: number;
  plainLanguageEn: string;
  plainLanguageEs: string;
  textEn: string;
  textEs: string;
};

type ServiceCategory = {
  id: number;
  text: string;
  description: string;
};

type ResponseServiceProvided = {
  id: string;
  categoryId: number;
  plainLanguage: string;
  text: string;
};

type GroupedServices = {
  categoryName: string;
  services: ResponseServiceProvided[];
}

type TypeOfHelpInputProps = {
  legend: string;
  isMobile: boolean;
  compact?: boolean;
  filters: SearchFilters;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
  selectedServices?: String[];
  setSelectedServices?: Dispatch<SetStateAction<String[]>>;
};

function TypeOfHelpInput({
  legend,
  isMobile,
  compact = false,
  filters,
  setFilters,
  selectedServices,
  setSelectedServices
}: TypeOfHelpInputProps) {
  const [responseProviders, setResponseProviders] = useState(new Array<GroupedServices>());
  const [desktopProviders, setDesktopProviders] = useState(new Array<ResponseServiceProvided>());

  const responseFn = (response: AxiosResponse) => {
    let categories: ServiceCategory[] = response.data?.serviceCategories.filter((category: ServiceCategory) => category.text !== "Unknown" && category.text !== "");
    let responseServices =
      response.data?.servicesProvided.reduce((acc: GroupedServices[], service: ApiServiceProvided) => {
        let index = -1, catIndex = acc.findIndex(c => c.categoryName === categories.find((category: ServiceCategory) => category.id === service.categoryId)?.text);
        if (catIndex === -1) return acc;
        if (!acc[catIndex].services)
          acc[catIndex].services = [];
        if (acc[catIndex].services.length > 0)
          index = acc[catIndex].services.findIndex(s => s.text === (i18n.language === "es" ? service.textEs : service.textEn));
        if (index === -1) {
          acc[catIndex].services.push({
            id: service.id.toString(),
            categoryId: service.categoryId,
            plainLanguage: i18n.language === "es" ? service.plainLanguageEs : service.plainLanguageEn,
            text: i18n.language === "es" ? service.textEs : service.textEn
          });
          return acc;
        } else {
          acc[catIndex].services[index].id += `,${service.id.toString()}`;
          return acc;
        }
      }, categories.map((category: ServiceCategory) => { //set initial accumulator value
        return {
          categoryName: category.text,
          services: []
        }
      }));
      setResponseProviders(responseServices);
      
      let desktopList: ResponseServiceProvided[] = [];
      responseServices.forEach((category: GroupedServices) => {
        desktopList = desktopList.concat(category.services);
      });
      setDesktopProviders(desktopList);

  }

  const errorFn = (error: any) => {
    console.error("Error fetching services provided:", error);
  }
  
  useEffect(() => {
    QueryLookupTablesApi(
      {
        IncludeServicesProvided: true,
        IncludeServiceCategories: true,
        language: i18n.language
      },
      new AbortController().signal,
      responseFn, errorFn);
  }, [i18n.language]);

  const { crisisSelectedValues, setSelectedValues } = useContext(CrisisContext);
  
  const setServiceProvidedFilter = (serviceProvided: ResponseServiceProvided) => {
    logEvent(AnalyticsAction.UpdateFilter, {
      label: "type of help",
      filter_type: "type of help",
      filter_value: serviceProvided.text,
    });
    setSelectedServices && setSelectedServices(
      toggleItemsInFilterList(
        selectedServices as string[], 
        [serviceProvided.text]));
    setFilters({
      ...filters,
      serviceProvidedIds: toggleItemsInFilterList(
        filters.serviceProvidedIds as string[], 
        serviceProvided.id.split(",")
        ) as [],
    });
    if (serviceProvided.categoryId === 1500103) {// Crisis services category
      setSelectedValues(
        toggleItemsInFilterList(
          crisisSelectedValues as string[],
          serviceProvided.id.split(",")
        )
      );
    }
  };

  return (
    <Fieldset legend={legend} legendStyle={compact ? "srOnly" : "large"}>
      {isMobile ? (responseProviders.sort((a, b) => a.categoryName.localeCompare(b.categoryName)).map((category: GroupedServices) => (
        <React.Fragment key={category.categoryName}>
          <h3 style={{"marginTop": ".6rem", "fontSize": "1.2rem"}}>{category.categoryName}</h3>
          {category.services.filter((service: ResponseServiceProvided) => !excludedServices.includes(service.plainLanguage))
            .sort((a, b) => a.text.localeCompare(b.text)).map((service: ResponseServiceProvided) => {
            return (
              <React.Fragment key={service.plainLanguage}>
                <FilterCheckbox
                  name={service.id.toString()}
                  value={service.id.toString()}
                  label={service.text}
                  customChecked={() => {return (
                    (filters.serviceProvidedIds ?? []).some(id => service.id.split(",").includes(id)) || 
                    (filters.serviceProvidedIds ?? []).includes(service.id)
                  )}}
                  selectedFilterValues={filters.serviceProvidedIds ?? []}
                  onChange={() => setServiceProvidedFilter(service)}
                  key={service.id.toString()}
                />
              </React.Fragment>
            );
          })}
        </React.Fragment>
      ))) : (desktopProviders.filter(service => serviceMatch.includes(service.text)).sort((a, b) => a.text.localeCompare(b.text)).map((option: ResponseServiceProvided) => (
        <React.Fragment key={option.plainLanguage}>
          <FilterCheckbox
            name={option.id.toString()}
            value={option.id.toString()}
            label={option.text}
            customChecked={() => {return (
              (filters.serviceProvidedIds ?? []).some(id => option.id.split(",").includes(id)) || 
              (filters.serviceProvidedIds ?? []).includes(option.id)
            )}}
            selectedFilterValues={filters.serviceProvidedIds ?? []}
            onChange={() => setServiceProvidedFilter(option)}
            key={option.id.toString()}
          />

          {/* {(option.text === "Walk-in crisis services" || option.text === "Servicios sin cita previa en caso de crisis") && 
            option.id.split(",").some(id => filters.serviceProvidedIds?.includes(id)) && (
              <div className="margin-y-2" id="crisis-content-box">
                <AppAlert Icon={Phone}>
                  <ReactMarkdown>{t("suicidalIdeationAlert")}</ReactMarkdown>
                </AppAlert>
              </div>
            )} */}
        </React.Fragment>
      )))}
    </Fieldset>
  );
}

export default TypeOfHelpInput;
